import { Box, ColoredDot, Flex } from 'styling/GlobalStyles'
import { BodyText, Head2, OtherText, TitleText } from 'styling/Typography'
import { AccommodationNotificationContainer, AvailabilityIndicator, Block, BlockContainer, BlockStatusesContainer, CampContainer, CampOverviewContainer, ColoredSquare, SectionIconContainer } from '../Accommodation.style'
import { ExclamationMarkIcon } from '../icons/ExclamationMarkIcon'
import { useState } from 'react'
import Chevron from 'assets/icons/Chevron'
import { WarningIcon } from '../icons/WarningIcon'
import useQueryPagination from 'hooks/useQueryPagination'
import { clearAccommodationInfo, getAccommodationStatistics, getCamps } from 'features/accommodation/accommodationSlice'
import { useSelector } from 'react-redux'
import { RootState } from 'store'
import { IGetAllCampsSingleResponse } from 'types/api/accommodationApi.interface'
import { BedIcon } from '../icons/BedIcon'
import { OccupiedBedIcon } from '../icons/OccupiedBedIcon'
import { BlockedIcon } from '../icons/BlockedIcon'
import { DirtyIcon } from '../icons/DirtyIcon'

export const AccomodationDashboard = () => {
    const [params, setParams] = useState({ page: 1, limit: 99 })
    const { camps, statistics } = useSelector((state: RootState) => state.accomodation)
    useQueryPagination({ fetchFunctions: [getCamps, getAccommodationStatistics], params, cleanUpFunctions: [clearAccommodationInfo] })


    const StatusCard = ({ name, count, color, icon, iconBackground }) => {
        return <Box height='105px' width='100%' style={{ borderRadius: '12px', padding: '1rem', borderLeft: '1.5px solid ' + color }} >
            <Flex width='100%' height='100%' center gap='1rem'>
                <SectionIconContainer color={iconBackground}>
                    {icon}
                </SectionIconContainer>
                <Flex column between>
                    <TitleText weight='bold' style={{ fontSize: 24 }} >{count}</TitleText>
                    <BodyText weight='bold' >{name}</BodyText>
                    <OtherText grey >Sub Text</OtherText>
                </Flex>
            </Flex>
        </Box>
    }

    const Notification = ({ type, title, description, time }) => {
        return <AccommodationNotificationContainer>
            <Flex gap='1rem' center>
                <ExclamationMarkIcon />
                <Flex column gap='0.5rem' >
                    <BodyText>{title || 'Capacity exceeded in North Camp Block A'}</BodyText>
                    <OtherText grey >{description || 'The number of occupants in North Camp Block A has exceeded the maximum capacity.'}</OtherText>
                </Flex>
            </Flex>
            <OtherText grey >{time || '5mins ago'}</OtherText>
        </AccommodationNotificationContainer>
    }

    const CampSection = ({ camp }: { camp: IGetAllCampsSingleResponse }) => {

        const getCampStats = () => {
            let rooms = 0
            let beds = 0
            let available = 0
            let needCleaning = 0
            let occupied = 0
            let blocked = 0

            const blocksStats = camp.blocks?.map(block => {
                let numberOfRooms = 0
                let numberOfBeds = 0
                let availableBeds = 0
                let dirtyBeds = 0
                let occupiedBeds = 0
                let blockedBeds = 0

                block.rooms?.forEach(room => {
                    numberOfRooms += 1
                    rooms += 1

                    room.beds?.forEach(bed => {
                        numberOfBeds += 1
                        beds += 1

                        switch (bed.state) {
                            case 1:
                                availableBeds += 1
                                available += 1
                                break
                            case 2:
                                occupiedBeds += 1
                                occupied += 1
                                break
                            case 3:
                                dirtyBeds += 1
                                needCleaning += 1
                                break
                            case 4:
                                blockedBeds += 1
                                blocked += 1
                                break
                        }
                    })
                })

                const occupancyPercentage = numberOfBeds
                    ? Math.round((occupiedBeds / numberOfBeds) * 100)
                    : 0

                return {
                    blockName: block.name,
                    numberOfRooms,
                    numberOfBeds,
                    totalBeds: numberOfBeds,
                    availableBeds,
                    dirtyBeds,
                    occupiedBeds,
                    blockedBeds,
                    occupancyPercentage
                }
            }) || []

            const occupancy = beds ? Math.round((occupied / beds) * 100) : 0

            return {
                rooms,
                beds,
                totalBeds: beds,
                occupancy,
                available,
                needCleaning,
                blocked,
                blocks: blocksStats
            }
        }


        const [active, setActive] = useState(false)
        return <CampContainer active={active} onClick={() => setActive(!active)} >
            <CampOverviewContainer>
                <Flex column between height='100%'>
                    <BodyText style={{ fontSize: 20 }} >{camp.name}</BodyText>
                    <BodyText grey >{camp.blocks?.length || 0} blocks · {getCampStats()?.rooms || 0} rooms · {getCampStats()?.beds} beds total</BodyText>
                </Flex>
                <Flex height='100%' gap='1rem' center >
                    <Flex center column height='100%' between >
                        <Head2 color={colors.occupied} >{getCampStats()?.occupancy || 0}%</Head2>
                        <BodyText grey >Occupancy</BodyText>
                    </Flex>

                    <Flex center column height='100%' between >
                        <Head2 color={colors.available} >{getCampStats()?.available || 0}</Head2>
                        <BodyText grey >Available</BodyText>
                    </Flex>

                    <Flex center column height='100%' between >
                        <Head2 color={colors.dirty} >{getCampStats()?.needCleaning || 0}</Head2>
                        <BodyText grey >Need Cleaning</BodyText>
                    </Flex>

                    <Chevron open={active} />
                </Flex>
            </CampOverviewContainer>
            <BlockContainer>
                {getCampStats()?.blocks.map((b, i) => <SingleBlock key={i} name={b.blockName} rooms={b.numberOfRooms} available={b.availableBeds} occupied={b.occupiedBeds} blocked={b.blockedBeds} dirty={b.dirtyBeds} occupancy={b.occupancyPercentage} totalBeds={b.totalBeds} />)}

            </BlockContainer>
        </CampContainer>
    }

    const SingleBlock = ({ rooms, available, occupied, blocked, dirty, occupancy, name, totalBeds }) => {
        return <Block>
            <Flex width='100%' between >
                <Flex column gap='0.5rem' height='100%' between>
                    <Flex gap='0.5rem' ><TitleText style={{ fontSize: 18 }} >{name || ''}</TitleText><WarningIcon /></Flex>
                    <BodyText grey >{rooms || 0} rooms · {totalBeds || 0} beds total</BodyText>
                </Flex>
                <BodyText weight='bold' style={{ fontSize: 20, color: colors.available }} >{occupancy || 0}%</BodyText>
            </Flex>
            <Flex mt='16' mb='16' gap='0.5rem' column >
                <Flex width='100%' between >
                    <OtherText grey >Availability</OtherText>
                    <OtherText grey >{available || 0} / {totalBeds || 0}</OtherText>
                </Flex>
                <AvailabilityIndicator color={colors.available} percentage={available && totalBeds ? Math.round((available / totalBeds) * 100) : 0} />
            </Flex>
            <BlockStatusesContainer>
                <BlockStatus name='Available' count={available || 0} iconBackground={colors.paleGreen} icon={<BedIcon color={colors.available} />} />
                <BlockStatus name='Occupied' count={occupied || 0} iconBackground={colors.paleBlue} icon={<OccupiedBedIcon />} />
                <BlockStatus name='Dirty' count={dirty || 0} iconBackground={colors.paleYellow} icon={<DirtyIcon />} />
                <BlockStatus name='Blocked' count={blocked || 0} iconBackground={colors.paleRed} icon={<BlockedIcon />} />
            </BlockStatusesContainer>
        </Block>
    }

    const BlockStatus = ({ name, count, icon, iconBackground }) => {
        return <Flex height='40px' width='100%' gap='0.5rem' center >

            <SectionIconContainer color={iconBackground}>
                {icon}
            </SectionIconContainer>
            <Flex column between >
                <TitleText  >{count}</TitleText>
                <BodyText grey >{name} </BodyText>
            </Flex>
        </Flex>
    }

    return <>
        <Flex mt='16' mb='16' gap='1rem' >
            <StatusCard name='TOTAL CAPACITY' color={colors.totalCapacity} count={statistics?.totalCapacity || 0} iconBackground={colors.paleOrange} icon={<BedIcon />} />
            <StatusCard name='AVAILABLE' color={colors.available} count={statistics?.available || 0} iconBackground={colors.paleGreen} icon={<BedIcon color={colors.available} />} />
            <StatusCard name='OCCUPIED' color={colors.occupied} count={statistics?.occupied || 0} iconBackground={colors.paleBlue} icon={<OccupiedBedIcon />} />
            <StatusCard name='DIRTY' color={colors.dirty} count={statistics?.occupied || 0} iconBackground={colors.paleYellow} icon={<DirtyIcon />} />
            <StatusCard name='BLOCKED' color={colors.blocked} count={statistics?.blocked || 0} iconBackground={colors.paleRed} icon={<BlockedIcon />} />
        </Flex>
        {/*      <Box>
            <Flex column gap='1rem' >
                <Notification type='warning' title='Capacity exceeded in North Camp Block A' description='The number of occupants in North Camp Block A has exceeded the maximum capacity.' time='5 mins ago' />
                <Notification type='warning' title='Capacity exceeded in South Camp Block B' description='The number of occupants in South Camp Block B has exceeded the maximum capacity.' time='10 mins ago' />
            </Flex>
        </Box>
        */}
        <Flex column gap='1rem'>
            {camps?.data.map(c => <CampSection camp={c} key={c.id} />)}
        </Flex>
    </>
}
const colors = {
    totalCapacity: '#ff9800',
    paleOrange: '#FF99001A',
    available: '#10B981',
    paleGreen: '#24D07B1A',
    occupied: '#51A2FF',
    paleBlue: '#00A6FF26',
    dirty: '#F4D03F',
    paleYellow: '#FF99001A',
    blocked: '#FF6467',
    paleRed: '#E74C3C1A',

}